import { ActionModal, FormError, Toggle } from '@kcftech/design-system';
import { ITimeRangeCreateRequest } from '@kcftech/prometheus-client';
import { Label } from 'components/forms/Label';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useState } from 'react';
import { Controller, RegisterOptions, useForm } from 'react-hook-form';
import { isValidNumber } from 'utils/number';

type TimeRangeForm = ITimeRangeCreateRequest;

export type CreateTimeRangeModalProps = {
    /**
     * Callback for when the confirm is clicked
     */
    onConfirm: (data: ITimeRangeCreateRequest) => Promise<void>;
    /**
     * Callback for when the close button is clicked
     */
    onClose: () => void;
};

export const maxTimeRangeSeconds = 31536000;
export const minTimeRangeSeconds = 1;
const descriptionMaxLength = 50;
const nameMaxLength = 5;

const timeInSecondsRules: RegisterOptions = {
    required: 'Please enter the time range',
    max: {
        value: maxTimeRangeSeconds,
        message: `Time cannot be greater than ${maxTimeRangeSeconds} seconds`,
    },
    min: {
        value: minTimeRangeSeconds,
        message: `Time cannot be less than ${minTimeRangeSeconds} second`,
    },
    validate: (value) =>
        !isValidNumber(value) || !Number.isInteger(Number(value)) || !value.trim()
            ? 'Time must be a positive whole number'
            : undefined,
};

const descriptionRules: RegisterOptions = {
    required: 'Please enter the description',
    maxLength: {
        value: descriptionMaxLength,
        message: `Description cannot exceed ${descriptionMaxLength} characters`,
    },
    validate: (value) =>
        !value.trim() ? 'Description must contain characters' : undefined,
};

const nameRules: RegisterOptions = {
    required: 'Please enter the button text',
    maxLength: {
        value: nameMaxLength,
        message: `Text cannot exceed ${nameMaxLength} characters`,
    },
    validate: (value) => (!value.trim() ? 'Text must contain characters' : undefined),
};

export function CreateTimeRangeModal({ onConfirm, onClose }: CreateTimeRangeModalProps) {
    const [saveError, setSaveError] = useState('');
    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<TimeRangeForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: { IsGlobal: false },
    });

    return (
        <ActionModal
            title="Create Time Range"
            actionButtonText="Create"
            closeButtonText="Cancel"
            height={750}
            width={1300}
            onClose={onClose}
            onAction={handleSubmit(async (data) => {
                setSaveError('');
                try {
                    await onConfirm({ ...data });
                } catch {
                    setSaveError('Time range could not be created');
                }
            })}
            actionable={(!isSubmitted || isValid) && !(isValidating || isSubmitting)}
            footerContent={<FormError message={saveError} />}
        >
            <div className="flex-column flex-grow-1">
                <ValidatableLabel
                    ariaLabel="time range button text"
                    label="Button Text"
                    error={errors.Name}
                    required
                >
                    <TextInput
                        placeholder="Enter button text"
                        invalid={errors.Name !== undefined}
                        {...register('Name', nameRules)}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    className="mt-20"
                    ariaLabel="time range input"
                    label="Time Range"
                    error={errors.TimeInSeconds}
                    required
                >
                    <TextInput
                        placeholder="Enter time range in seconds"
                        invalid={errors.TimeInSeconds !== undefined}
                        {...register('TimeInSeconds', timeInSecondsRules)}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    className="mt-20"
                    ariaLabel="time range tooltip description"
                    label="Tooltip Description"
                    error={errors.Description}
                    required
                >
                    <TextInput
                        placeholder="Enter tooltip description"
                        invalid={errors.Description !== undefined}
                        {...register('Description', descriptionRules)}
                    />
                </ValidatableLabel>
                <Label className="mt-20" label="Add to All Corporations" />
                <Controller
                    control={control}
                    name="IsGlobal"
                    render={({ field: { value: isGlobal, onChange } }) => (
                        <Toggle checked={isGlobal} onChange={onChange} />
                    )}
                />
            </div>
        </ActionModal>
    );
}
